/* "My Decks" — the deck list, and the four things you can do to one (#48).
 *
 * A dialog off the header's deck chip rather than a fourth tab. The tabs are
 * *places you work*; picking which deck you are working on is a thing you do
 * for two seconds and then leave. It is also the smaller surface to redraw
 * when the frontend redesign lands.
 *
 * Everything here goes through `data/deckstore.js`, which decides whether that
 * means `localStorage` or the `decks` table. This file never learns which, and
 * never gates anything behind an account: signed out you get exactly the same
 * list and the same four actions, they just live in the browser. The only
 * visible difference is one line of footer text telling you where they are.
 */
import { h, fill, toast } from './dom.js';
import { emptyDeck, totalCards, validate, DECK_SIZE } from '../data/deck.js';
import {
  listDecks, createDeck, renameDeck, duplicateDeck, deleteDeck,
  activeDeckId, setActiveDeckId, isRemote,
} from '../data/deckstore.js';

/**
 * @param {object} app  the app shell — needs `deck`, and `openDeck(id, deck)`
 *                      to actually switch what the builder is editing.
 */
export function createDecksDialog(app) {
  let decks = [];
  let busy = false;
  let loaded = false;
  let error = null;

  const body = h('div.decksbody');
  const root = h('dialog.decksdlg', {
    'aria-labelledby': 'decks-title',
    // Clicking the backdrop lands on the dialog element itself, never a child.
    onclick: e => { if (e.target === root) close(); },
    onclose: () => { error = null; busy = false; },
  }, body);

  /* ------------------------------------------------------------------ */

  /** Run a store call with the list locked, then refresh from the store. */
  async function run(fn, { after } = {}) {
    if (busy) return;
    busy = true; error = null; render();

    const r = await fn();
    busy = false;

    if (!r.ok) { error = r.error; render(); return r; }
    await refresh();
    after?.(r);
    return r;
  }

  async function refresh() {
    const r = await listDecks();
    if (r.ok) { decks = r.decks; loaded = true; error = null; } else { error = r.error; }
    render();
    return r;
  }

  /* ------------------------------------------------------------------ */
  /* actions                                                             */
  /* ------------------------------------------------------------------ */

  function switchTo(deck) {
    setActiveDeckId(deck.id);
    app.openDeck(deck.id, { name: deck.name, leader: deck.leader, cards: { ...deck.cards } });
    close();
  }

  const onNew = () => run(() => createDeck({ ...emptyDeck(), name: 'New deck' }), {
    after: r => switchTo(r.deck),
  });

  function onRename(deck) {
    /* `prompt` rather than an inline field, deliberately: this dialog is
     * already modal, and a second modal layer inside it is where focus
     * management goes wrong. Worth revisiting with the redesign. */
    const next = globalThis.prompt?.('Deck name', deck.name || '');
    if (next == null || next === deck.name) return;
    return run(() => renameDeck(deck.id, next));
  }

  const onDuplicate = deck => run(() => duplicateDeck(deck.id), {
    after: () => toast('Deck duplicated.', 'good'),
  });

  function onDelete(deck) {
    const label = deck.name || 'this deck';
    if (!globalThis.confirm?.(`Delete ${label}? This cannot be undone.`)) return;
    const wasActive = String(deck.id) === String(activeDeckId());
    return run(() => deleteDeck(deck.id), {
      after: () => {
        toast('Deck deleted.', 'good');
        /* Deleting the deck you were editing has to leave you somewhere. The
         * next one in the list, or an empty deck if that was the last. */
        if (!wasActive) return;
        const next = decks[0];
        if (next) switchTo(next);
        else { setActiveDeckId(null); app.openDeck(null, emptyDeck()); }
      },
    });
  }

  /* ------------------------------------------------------------------ */
  /* render                                                              */
  /* ------------------------------------------------------------------ */

  function deckRow(deck) {
    const active = String(deck.id) === String(activeDeckId());
    const n = totalCards(deck);
    const legal = validate(deck).length === 0;

    return h('div.deckrow', { class: active ? 'is-active' : '' },
      h('button.deckpick', {
        type: 'button',
        disabled: busy,
        'aria-current': active ? 'true' : null,
        onclick: () => (active ? close() : switchTo(deck)),
      },
        h('span.dot', { style: { background: legal ? 'var(--ok)' : 'var(--tx-6)' } }),
        h('span.nm', deck.name || 'Untitled deck'),
        h('span.meta', `${n}/${DECK_SIZE}${legal ? ' · legal' : ''}`),
        active ? h('span.badge', 'OPEN') : null,
      ),
      h('div.deckacts',
        h('button.btn', { type: 'button', disabled: busy, title: 'Rename', onclick: () => onRename(deck) }, 'Rename'),
        h('button.btn', { type: 'button', disabled: busy, title: 'Duplicate', onclick: () => onDuplicate(deck) }, 'Copy'),
        h('button.btn', { type: 'button', disabled: busy, title: 'Delete', onclick: () => onDelete(deck) }, 'Delete'),
      ),
    );
  }

  function render() {
    const where = isRemote()
      ? 'Saved to your account — these follow you between browsers.'
      : 'Saved in this browser. Sign in and they follow you between devices.';

    fill(body,
      h('div.deckshead',
        h('h2', { id: 'decks-title' }, 'My Decks'),
        h('button.btn', { type: 'button', 'aria-label': 'Close', onclick: close }, '×'),
      ),

      error ? h('div.authmsg.is-bad', error) : null,

      /* Its own class rather than the shared `.empty-note`: this dialog lives
       * in the document while closed, and an e2e spec asserting on the app's
       * one empty note found two. */
      h('div.decklist',
        !loaded ? h('div.decksempty', 'Loading…')
          : decks.length ? decks.map(deckRow)
            : h('div.decksempty', 'No saved decks yet. Start one below.'),
      ),

      h('div.decksfoot',
        h('button.btn-pri', { type: 'button', disabled: busy, onclick: onNew }, 'New deck'),
        h('div.authhint', where),
      ),
    );
  }

  /* ------------------------------------------------------------------ */

  function open() {
    render();                 // paint the shell before the list arrives
    if (!root.open) root.showModal?.();
    refresh();
    return root;
  }

  const close = () => { if (root.open) root.close(); else root.removeAttribute?.('open'); };

  /* Not rendered until opened. The dialog sits in the document from boot, and
   * painting a list nobody has asked for puts its rows into every query the
   * page runs against itself. */
  return { root, open, close, refresh };
}
